<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceGenerationInvoiceLines',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ts = ref(null);
  const stockColumns = ref([
    {
      field: 'partner',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Partner'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'partner_id',
        },
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Rental'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        navigateOnIconClick: true,
        params: {
          id: 'rental_id',
        },
      },
    },
    {
      field: 'site',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Site'),
      width: 90,
    },
    {
      field: 'item',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Item'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'product',
        navigateOnIconClick: true,
        params: {
          id: 'product_id',
        },
      },
    },
    {
      field: 'serial_number',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Serial'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'meter',
        navigateOnIconClick: true,
        params: {
          id: 'meter_id',
        },
      },
    },
  ]);
  const values = ref(null);
  const dataColumnFormatter = ref({
    name: 'template',
    format:
      '<div class="d-flex flex-column lh-sm">\n\
  {{ $value && $value.net != null ? `<span class="fw-semibold">${$fmt.number($value.net, { decimals: 2 })}</span>` : \'\' }}\n\
  {{ $value && $value.quantity != null ? `<span class="text-muted small">${$fmt.number($value.quantity)} ${unit || \'\'}</span>` : \'\' }}\n\
</div>\n\
',
  });
  const ux = ref([]);
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_groupBy = computed(() => [
    'partner',
    {
      field: 'rental',
      emptyLabel: '',
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ts: ts.value,
    stockColumns: stockColumns.value,
    values: values.value,
    dataColumnFormatter: dataColumnFormatter.value,
    ux: ux.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_groupBy: b_2248226175642056_groupBy.value,
  }));

  const $$load_values = () => {
    return $fn.value.fetch('view_draft_invoice_lines').then((p) =>
      _.extend(p, {
        columns: stockColumns.value.concat(
          p.columns.map((field) => ({
            field,
            name: field,
            align: 'right',
            formatter: dataColumnFormatter.value,
          }))
        ),
      })
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ts' },
      { name: 'stockColumns' },
      { name: 'values' },
      { name: 'dataColumnFormatter' },
      { name: 'ux' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_groupBy', nodeId: '2248226175642056' },
    ],
    loaders: { values: $$load_values },
    ux: {},
    dataRefs: {
      selectedItems,
      ts,
      stockColumns,
      values,
      dataColumnFormatter,
      ux,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_groupBy,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_2248226175642056_setData_8yevsdb3p_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_tqo9g57mp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.id,
        modal: true,
      },
    };
  };
  const inst_evh_2248226175642056_command_zl0hdywsv_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice_number';
  };
  const act_4615685607825208_reloadGrid_dlxkbyizz_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'draftInvoices',
    };
  };
  const act_4615685607825208_request_fpxmzh03l_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_draft_invoices',
      data: {
        invoices: selectedItems.value.map((p) => p.id),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_1tlvtyxmv =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_sdblta4hm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1tlvtyxmv,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_sdblta4hm],
    event: 'reload',
  });
  const act_2248226175642056_setData_8yevsdb3p = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_8yevsdb3p_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_q7sb23xdu =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_8yevsdb3p,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_q7sb23xdu],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_tqo9g57mp =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_tqo9g57mp_getActionArgs,
    });
  const inst_evh_2248226175642056_command_zl0hdywsv =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_tqo9g57mp,
      event: 'command',
      when: inst_evh_2248226175642056_command_zl0hdywsv_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_zl0hdywsv],
    event: 'command',
  });
  const act_4615685607825208_reloadGrid_dlxkbyizz =
    new actionTypes.ReloadGridAction({
      actionArgs: act_4615685607825208_reloadGrid_dlxkbyizz_getActionArgs,
    });
  const inst_evh_4615685607825208_success_w5v4s937e =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadGrid_dlxkbyizz,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_4615685607825208_request_fpxmzh03l = new actionTypes.RequestAction({
    actionArgs: act_4615685607825208_request_fpxmzh03l_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_4615685607825208_success_w5v4s937e],
  });
  const inst_evh_4615685607825208_click_1g4o0zw9v =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_request_fpxmzh03l,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_1g4o0zw9v],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_sdblta4hm,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_q7sb23xdu,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_zl0hdywsv,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_success_w5v4s937e,
    inst_evh_4615685607825208_click_1g4o0zw9v,
    deleteItems: inst_evh_4615685607825208_click_1g4o0zw9v,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_reload_sdblta4hm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_sdblta4hm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_q7sb23xdu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_q7sb23xdu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_zl0hdywsv = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_zl0hdywsv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_success_w5v4s937e = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_success_w5v4s937e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_1g4o0zw9v = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_1g4o0zw9v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="draftInvoiceLines"
      :headerTitle="gettext('Draft invoice lines')"
      :items="values.rows"
      checkbox-row-selection
      footer-totals
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'draft-invoice-lines')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="values.columns"
      :row-height="44"
      :groupBy="b_2248226175642056_groupBy"
      class="page--grid"
      ref="grid"
      v-if="values"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            icon="fa-trash"
            :text="gettext('Delete')"
            size="sm"
            variant="light"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
        </div>
      </template>
    </ItpDataGrid>
  </ItpUsercontrol>
</template>
